import React from "react";

import { useUIStateStore } from "@/store/uiStateStore";

import { cn } from "../utils/utils";

type HeadingElements = "h1" | "h2" | "h3" | "h4";
type HeadingProps = {
  children: React.ReactNode;
  as?: HeadingElements;
  ref?: React.Ref<HTMLHeadingElement>;
} & React.HTMLAttributes<HTMLHeadingElement>;

const Heading: React.FC<HeadingProps> = ({
  children,
  as = "h1",
  className = "",
  ...props
}) => {
  const isTunecore = useUIStateStore(state => state.isTunecore);

  const baseStyle = isTunecore
    ? "text-white font-tunecore"
    : "text-sentric-black-text font-sentric";

  const styles = isTunecore
    ? {
        h1: cn(baseStyle, "text-[2rem] font-black leading-[1.167] tracking-[-0.025rem]"),
        h2: (wordTooLong: boolean) =>
          cn(
            baseStyle,
            wordTooLong
              ? "text-[1.3rem] flex-shrink font-black"
              : "text-[1.3rem] lg:text-[1.5rem] flex-shrink font-black",
          ),
        h3: cn(baseStyle, "text-[1.3rem] font-black"),
        h4: cn(baseStyle, "text-[1rem] font-bold"),
      }
    : {
        h1: cn(baseStyle, "text-size-h1 font-medium leading-110 tracking-tightest"),
        h2: (wordTooLong: boolean) =>
          cn(baseStyle, `${wordTooLong ? "text-size-h3" : "text-size-h3 lg:text-size-h2"} flex-shrink font-medium tracking-tighter`),
        h3: cn(baseStyle, "text-size-h3 font-normal leading-9 tracking-tight"),
        h4: cn(baseStyle, "text-size-h4 font-normal"),
      };

  const Component = as;

  const headingText = typeof children === "string" ? children : "";
  const wordTooLong = headingText.length > 40;

  const computedClassName
    = typeof styles[as] === "function"
      ? (styles[as] as (wordTooLong: boolean) => string)(wordTooLong)
      : styles[as];

  return (
    <Component className={cn(computedClassName, className)} {...props}>
      {children}
    </Component>
  );
};

export default Heading;
